/**
 * api.js — Tiny client for /api/club/* endpoints.
 *
 * Uses the shared resolveBackendUrl() helper so linked custom domains
 * (lesconcierge.com · kvb.mindescapes.in) call same-origin and the backend
 * sees the right Host header for tenant resolution.
 *
 * Preview-only tenant override: if the browser URL carries `?_tenant=kvb`,
 * we forward that param on every API call so the backend resolves to the
 * correct tenant in the absence of a real Host header.
 *
 * FIR-20260611-001
 */

import { BACKEND_URL as BASE } from "../../lib/backend-url";

function _tenantOverride() {
  if (typeof window === "undefined") return null;
  // Persist the override into sessionStorage once read so internal navigation keeps it
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("_tenant");
    if (t) {
      sessionStorage.setItem("club_tenant_override", t);
      return t;
    }
    return sessionStorage.getItem("club_tenant_override");
  } catch {
    return null;
  }
}

function _withTenant(url) {
  const t = _tenantOverride();
  if (!t) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}_tenant=${encodeURIComponent(t)}`;
}

async function jsonOrThrow(res) {
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || JSON.stringify(j);
    } catch (e) { /* keep statusText */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function getConfig() {
  return jsonOrThrow(await fetch(_withTenant(`${BASE}/api/club/config`)));
}

export async function getMe(memberCardNo) {
  const q = memberCardNo ? `?member=${encodeURIComponent(memberCardNo)}` : "";
  return jsonOrThrow(await fetch(_withTenant(`${BASE}/api/club/me${q}`)));
}

export async function authMember({ first, last, card, email, password }) {
  return jsonOrThrow(await fetch(_withTenant(`${BASE}/api/club/auth`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ first, last, card, email, password }),
  }));
}

export async function listClubs({ member, country, city, type, amenity, search, limit = 60, offset = 0 } = {}) {
  const p = new URLSearchParams();
  if (member) p.set("member", member);
  if (country) p.set("country", country);
  if (city) p.set("city", city);
  if (type) p.set("type", type);
  if (amenity) p.set("amenity", amenity);
  if (search) p.set("search", search);
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  return jsonOrThrow(await fetch(_withTenant(`${BASE}/api/club/clubs?${p.toString()}`)));
}

export async function getClub(idShort, memberCardNo) {
  const q = memberCardNo ? `?member=${encodeURIComponent(memberCardNo)}` : "";
  return jsonOrThrow(await fetch(_withTenant(`${BASE}/api/club/clubs/${idShort}${q}`)));
}

export async function submitRequest(payload) {
  return jsonOrThrow(await fetch(_withTenant(`${BASE}/api/club/requests`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }));
}

export async function signup(payload) {
  return jsonOrThrow(await fetch(_withTenant(`${BASE}/api/club/signup`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }));
}

export function clearTenantOverride() {
  try { sessionStorage.removeItem("club_tenant_override"); } catch { /* ignore */ }
}
